import { CROCKFORD_ALPHABET, CODE_LENGTH } from './crockford.js';
/**
 * 码载荷的定长文本格式（地点码与商品码共用）：
 *
 * ```text
 *   位  0        1        2        3..10        11..14
 *       ┌────────┬────────┬────────┬────────────┬──────────────┐
 *       │ 魔数   │ 版本   │ 类型   │ 8 位码     │ 4 位校验位   │
 *       │  'C'   │  '1'   │ 'S'/'P'│ Crockford  │ Crockford    │
 *       └────────┴────────┴────────┴────────────┴──────────────┘
 *       └──────────────── 校验位覆盖这 11 位 ────────────────┘
 * ```
 *
 * **为什么是定长串而不是 URL 或 JSON**：见本任务开头的「规格缺口 1」。
 * 定长带来两个可验收的性质：① 载荷长度不随业务数据变化，二维码版本稳定；
 * ② 「商品码不含地点/货架」成为**结构性事实**（没有可变长字段可以夹带），
 * 而不是靠约定——Task 4 的纯净性断言正是建立在这两点上。
 */
/** 载荷版本。它是载荷第 1 位的十进制字符；协议升级时**新增**版本，不改旧版本语义（契约 §1.1 的版本策略同理）。 */
export const PAYLOAD_VERSION = 1;
/** 魔数：位 0。用来把「本系统的码」与任意 15 字符输入区分开，扫到别家的码时给 MALFORMED 而不是瞎猜。 */
export const PAYLOAD_MAGIC = 'C';
/** 定长 15 位 = 1 魔数 + 1 版本 + 1 类型 + 8 码 + 4 校验位 */
export const PAYLOAD_LENGTH = 15;
/** 校验位长度（位） */
export const CHECKSUM_LENGTH = 4;
/**
 * 类型位（位 2）。
 *
 * **为什么地点是 `S` 而不是 `L`**：Crockford 字母表为排除易混字符而不含 `I L O U`，
 * 用 `L` 会让载荷自身无法通过字符集校验（实测：任何 `C1L…` 一律被判 MALFORMED）。
 * `S` 取 Site（地点），`P` 取 Product（商品），两者都在字母表内。
 */
export const CODE_TYPE_LETTERS = { location: 'S', product: 'P' };
/** CRC32（IEEE 802.3，反射多项式 0xEDB88320）查表 */
const CRC32_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i += 1) {
        let c = i;
        for (let bit = 0; bit < 8; bit += 1)
            c = (c & 1) === 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        table[i] = c >>> 0;
    }
    return table;
})();
/** CRC32 原值（无符号 32 位）。与契约 §20.5 的订单码校验位同款算法。 */
export function crc32(input) {
    let crc = 0xffffffff;
    for (let i = 0; i < input.length; i += 1) {
        // 下标 `& 0xff` 恒落在 0..255，`?? 0` 只是为 `noUncheckedIndexedAccess` 收敛类型，
        // 运行时不可达；用 `?? 0` 而不是 `as number` 是为了不谎报「这里可能越界」。
        crc = (CRC32_TABLE[(crc ^ input.charCodeAt(i)) & 0xff] ?? 0) ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}
/**
 * 校验位：CRC32 取**低 20 bit**，编码为 4 位 Crockford Base32（4 × 5 bit = 20 bit 恰好装满）。
 *
 * 与契约 §20.5 的口径一致（那里是订单码的 4 位 `CRC4`），这样全系统的校验位只有一套算法。
 * CRC 不是防伪签名：它识别「扫错码」与「载荷损坏」，防伪由「服务端权威数据」兜底。
 */
export function checksum4(prefix) {
    const value = crc32(prefix) & 0xfffff;
    let out = '';
    for (let shift = 15; shift >= 0; shift -= 5) {
        out += CROCKFORD_ALPHABET.charAt((value >>> shift) & 31);
    }
    return out;
}
/** 追加校验位，得到完整载荷 */
export function withChecksum(prefix) {
    return prefix + checksum4(prefix);
}
/**
 * 编码期断言：码必须恰好 8 位且落在字母表内。
 *
 * 这里**抛错而不是返回失败结果**：入参来自数据库或调用方代码，非法说明是编程/数据错误，
 * 而不是「扫到了一个坏码」。错误信息形如 `INVALID_LOCATION_CODE_LENGTH`，与
 * `02-architecture.md` §9.1/§9.2 设计示意里的错误串一致。
 */
function assertCode(code, subject) {
    if (code.length !== CODE_LENGTH)
        throw new Error(`INVALID_${subject}_CODE_LENGTH`);
    for (const ch of code) {
        if (!CROCKFORD_ALPHABET.includes(ch))
            throw new Error(`INVALID_${subject}_CODE_CHARSET`);
    }
}
/** 编码：`{ type, id }` → 定长载荷串 */
export function encodePayload(input) {
    assertCode(input.id, input.type.toUpperCase());
    return withChecksum(`${PAYLOAD_MAGIC}${PAYLOAD_VERSION}${CODE_TYPE_LETTERS[input.type]}${input.id}`);
}
/** 编码地点码（对外暴露语义化包装，避免调用方手拼类型位） */
export function encodeLocationPayload(locationCode) {
    return encodePayload({ type: 'location', id: locationCode });
}
/**
 * 地点码的 URL 形态：`https://<host>/s/<自重载荷>`，例如
 * `https://cuito.plus4ce.com/s/C1S7QK3M2XBD76Q`。
 *
 * **末段是自重载荷，不是裸 8 位码**（装配裁决 ①，契约 §6.1 的 `entryUrl` 示例即此形）：
 * ① 手机**系统相机**能直接打开，满足 `01-requirements.md` §8.2/§8.3 的形态要求与
 *    `REQ-SHOPSITE-001`「扫地点码直接进入购物站」——裸载荷串会被相机当成纯文本；
 * ② 载荷自带版本位与校验位，能挡住印刷模糊与残码（`07-roadmap.md` §1.4 要求）；
 * ③ `decodePayload` 本就兼容「URL 末段即载荷」，所以解析端拿到整条 URL 即可直接解。
 *
 * 入参仍是**裸 8 位码**（调用点最自然的写法），编码在本函数内部完成。
 * 裸 8 位码形态仍被解析端点接受，供**人工输入**与旧版印刷码使用 ——
 * 但它不含类型位，因此「裸码用错端点」只能得到 404，只有载荷形态才能判出
 * `422 CODE_TYPE_MISMATCH`（见 Task 5/6）。
 *
 * `host` 由调用方从配置注入，本包不读环境变量，保持纯函数。
 */
export function buildLocationCodeUrl(host, locationCode) {
    return `https://${host}/s/${encodeLocationPayload(locationCode)}`;
}
/**
 * 编码商品码。
 *
 * **签名刻意只有一个参数**：商品码不含地点、不含货架（`03-data-model.md` §4.4），
 * 货架位由服务端按顾客当前地点反查后，快照进**订单行**（`OrderLine.shelfId` + `shelfCode`）。
 * 少一个位置参数就少一条「顺手把地点写进码里」的路径。
 */
export function encodeProductPayload(productCode) {
    return encodePayload({ type: 'product', id: productCode });
}
/**
 * 商品码的 URL 形态：`https://<host>/p/<自重载荷>`，例如
 * `https://cuito.plus4ce.com/p/C1P9F2K7QW11VNF`。
 *
 * 与 `buildLocationCodeUrl` 同构（末段是载荷，理由见该函数的注释）。
 * **只带商品标识**：载荷里**没有**地点与货架 —— 货架位由服务端按「当前地点」
 * 解析（`GET/POST /codes/resolve/product/:code` 的 `atLocation.shelf.code`），
 * 因此同一个商品码在不同地点会得到不同的货架位，这是设计而非缺陷。
 */
export function buildProductCodeUrl(host, productCode) {
    return `https://${host}/p/${encodeProductPayload(productCode)}`;
}
/**
 * 取出载荷本体：允许「URL 的最后一个路径段就是载荷」（扫描器可能回传完整 URL），
 * 允许前后空白。**不做任何大小写变换。**
 *
 * **URL 末尾的 `?query` / `#fragment` 必须先剥掉再取末段**（计划附录 B 偏差 34 F1）：
 * 扫码宿主常会往打开的链接上追加来源标记（`?from=qr`）或锚点，不剥掉的话它们会并进
 * 「最后一个路径段」，让 15 位自重载荷直接沦为 `MALFORMED` ——
 * 自重校验本该挡住印刷模糊与残码，而不是宿主自己加的埋点参数。
 *
 * 路径前缀（`/s/`、`/p/`）**不参与类型判定**——类型只认载荷自带的类型位，
 * 避免「前端路由名」与「码类型」两套真源打架。
 *
 * **为什么必须有一个「不改大小写」的入口**（品质审查 C1，2026-09-28 实测）：
 * 订单码的货架位标识允许小写与 CJK（`SHELF_CODE_PATTERN`），而它的 CRC 是对**原文**算的。
 * 解码时若先把整串大写化再去比校验位，`shelfCode: 'a3'` 这种合法码会连自己的校验位都对不上
 * —— 编码端产出 `…|a3|…|XXXX`，解码端却拿 `…|A3|…` 重算，恒 `ORDER_CODE_INVALID`
 * （**自己编的码自己解不开**）。而且它不只是解析失败：即便侥幸通过，返回给收银端的
 * `shelfCode` 也已被改写，与 `OrderLine` 的快照不一致（挑拣清单会指错货位）。
 */
export function extractPayload(value) {
    const trimmed = value.trim();
    // URL 判定必须**大小写不敏感**：这里刻意还没做大写化（`normalizeToken` 以前是「先 upper 再判」），
    // 若沿用 `/^HTTPS?:\/\//`，`https://…` 会漏判，整条 URL 会被当成载荷 → MALFORMED。
    if (!/^https?:\/\//i.test(trimmed))
        return trimmed;
    // 先剥查询串/锚点，再切路径段：`…/s/C1S…D76Q?from=qr` 的末段才是载荷本身
    const withoutQuery = trimmed.split(/[?#]/)[0] ?? '';
    const segments = withoutQuery.split('/').filter((segment) => segment.length > 0);
    return segments[segments.length - 1] ?? '';
}
/**
 * 归一化：`extractPayload` + **整串大写化**。定长载荷（地点码/商品码）继续用它 ——
 * 它们的字段全部落在大写 Crockford 字母表内，大写化只是容忍人工输入的小写。
 *
 * **订单码不能用它**（品质审查 C1）：订单码的 `shelfCode` 允许小写与 CJK，
 * 而 CRC 覆盖原文 —— 见 `extractPayload` 的说明。
 */
export function normalizeToken(value) {
    return extractPayload(value).toUpperCase();
}
function failure(reason) {
    return { type: null, v: null, reason };
}
/**
 * 解码。校验顺序**依次**为：定长 → 字符集 → 魔数 → 版本 → 类型位 → 校验位 → 期望类型。
 *
 * 顺序是刻意固定的（与契约 §20.7 的订单码离线校验步骤同构）：
 * 先判「这是不是本系统的码」，再判「版本认不认识」，最后才做完整性比对 ——
 * 这样「版本升级」与「载荷损坏」会得到**不同**的失败原因，便于灰度期排查。
 *
 * @param expectedType 调用方要求的类型；不符时返回 `TYPE_MISMATCH` 且**不带出**另一类型的 id。
 */
export function decodePayload(value, expectedType) {
    // 非字符串入参一律按「坏码」处理（计划附录 B 偏差 34 F4）：HTTP 层把可选字段
    // （`undefined` / `null`）直接透传时，归一化里的 `.trim()` 会抛 `TypeError`，
    // 于是「参数缺失」被报成 500。语义上它本来就是「这个码不可用」，与其它坏码同路径。
    if (typeof value !== 'string')
        return failure('MALFORMED');
    const token = normalizeToken(value);
    if (token.length !== PAYLOAD_LENGTH)
        return failure('MALFORMED');
    for (const ch of token) {
        if (!CROCKFORD_ALPHABET.includes(ch))
            return failure('MALFORMED');
    }
    if (token.charAt(0) !== PAYLOAD_MAGIC)
        return failure('MALFORMED');
    const versionLetter = token.charAt(1);
    if (versionLetter !== String(PAYLOAD_VERSION))
        return failure('UNSUPPORTED_VERSION');
    const typeLetter = token.charAt(2);
    const type = Object.keys(CODE_TYPE_LETTERS).find((candidate) => CODE_TYPE_LETTERS[candidate] === typeLetter);
    if (!type)
        return failure('MALFORMED');
    const body = token.slice(0, PAYLOAD_LENGTH - CHECKSUM_LENGTH);
    if (token.slice(PAYLOAD_LENGTH - CHECKSUM_LENGTH) !== checksum4(body)) {
        return failure('CHECKSUM_MISMATCH');
    }
    if (expectedType !== undefined && expectedType !== type)
        return failure('TYPE_MISMATCH');
    const id = token.slice(3, 3 + CODE_LENGTH);
    return type === 'location'
        ? { type: 'location', locationId: id, v: PAYLOAD_VERSION }
        : { type: 'product', productId: id, v: PAYLOAD_VERSION };
}
//# sourceMappingURL=payload.js.map